import { keyframes } from '@emotion/react';
import type {
  CenterProps,
  GridProps,
  MotionPropsFor,
  StackProps,
} from '@hover/blueprint';
import {
  Center,
  Grid,
  VStack,
  cssVar,
  cssVariables,
  motion,
  useTheme,
} from '@hover/blueprint';
import { MotionConfig } from 'framer-motion';
import type { PropsWithChildren } from 'react';
import React, { useMemo, useState } from 'react';

import {
  useEmbedViewportSize,
  useRoutes,
  useStickyDepth,
} from 'src/modules/homeownerApp/hooks';
import { SafeRemoveScroll } from 'src/shared/components';
import { useIsEmbed } from 'src/shared/hooks';

import {
  CustomizeLayoutProvider,
  useCustomizeLayout,
} from './CustomizeLayoutContext';

export interface CustomizeLayoutProps extends GridProps {
  fromSample?: boolean;
}

const fadeIn = keyframes`
  0% { background: transparent };
  100% { background ${cssVariables.color('neutral.900')} }
`;

const CustomizeLayoutGrid = ({
  children,
  bg,
  background,
  fromSample,
  gridTemplateRows,
  gridTemplateColumns,
  ...props
}: CustomizeLayoutProps) => {
  const { isExitingToFullscreen, initial } = useCustomizeLayout();
  const isEmbed = useIsEmbed();

  const zIndex = useStickyDepth() + 1;

  const { height: embedViewportHeight } = useEmbedViewportSize({
    fromSample, // TODO https://hoverinc.atlassian.net/browse/MEOW-5669
  });

  const mobileTemplateAreas = `
    "image"
    "selector"
  `;

  const tabletTemplateAreas = `
    "image selector"
  `;

  return (
    <SafeRemoveScroll enabled={isEmbed ? false : undefined}>
      <Grid
        animation={initial ? `${fadeIn} 0.3s ease-out` : undefined}
        bg={isExitingToFullscreen ? 'transparent' : background ?? bg}
        bottom="0"
        gridTemplateAreas={{
          base: mobileTemplateAreas,
          tablet: tabletTemplateAreas,
        }}
        gridTemplateColumns={
          typeof gridTemplateColumns === 'string'
            ? gridTemplateColumns
            : {
                base: '100%',
                tablet: '55% 45%',
                ...gridTemplateColumns,
              }
        }
        gridTemplateRows={
          typeof gridTemplateRows === 'string'
            ? gridTemplateRows
            : {
                base: 'minmax(10rem, 1fr) min-content',
                tablet: embedViewportHeight.tablet,
                ...gridTemplateRows,
              }
        }
        left="0"
        pb={{ base: 0, tablet: 0 }}
        position="fixed"
        right="0"
        top="0"
        zIndex={zIndex}
        {...props}
      >
        {children}
      </Grid>
    </SafeRemoveScroll>
  );
};

export const CustomizeLayout = (props: CustomizeLayoutProps) => {
  const { isFirstRoute } = useRoutes();
  const [isLayoutAnimationComplete, setIsLayoutAnimationComplete] =
    useState(isFirstRoute);

  const onLayoutAnimationComplete = () => {
    setIsLayoutAnimationComplete(true);
  };

  const {
    motion: {
      framer: { soft, firm },
    },
  } = useTheme();

  // Apply custom transitions to the drawer and image when animating from
  // another route to the customize view, but once that animation is complete,
  // use a transition consistent with the rest of the layout animations for the
  // painting animation
  const transitions = useMemo(
    () => ({
      layout: firm,
      image: isLayoutAnimationComplete ? firm : { ...soft, damping: 11 },
      drawer: { ...soft, damping: 10, stiffness: 100 },
    }),
    [firm, soft, isLayoutAnimationComplete],
  );

  return (
    <CustomizeLayoutProvider
      isLayoutAnimationComplete={isLayoutAnimationComplete}
      onLayoutAnimationComplete={onLayoutAnimationComplete}
      transitions={transitions}
    >
      <CustomizeLayoutGrid {...props} />
    </CustomizeLayoutProvider>
  );
};

export interface CustomizeLayoutImageProps
  extends MotionPropsFor<CenterProps> {}

const MotionCenter = motion(Center);

export const CustomizeLayoutImage = (props: CustomizeLayoutImageProps) => {
  const { layout } = useCustomizeLayout();

  return (
    <MotionCenter
      boxSizing="border-box"
      gridArea="image"
      height="100%"
      layout={layout}
      position="relative"
      width="100%"
      {...props}
    />
  );
};

export interface CustomizeLayoutPanelProps extends MotionPropsFor<StackProps> {
  fromSample?: boolean;
}

const MotionVStack = motion(VStack);

const $drawerSlide = cssVar('customize-drawer-slide');

export const CustomizeLayoutPanel = ({
  fromSample,
  ...props
}: CustomizeLayoutPanelProps) => {
  const { height: embedViewportHeight } = useEmbedViewportSize({
    fromSample, // TODO https://hoverinc.atlassian.net/browse/MEOW-5669
  });

  const { transitions } = useCustomizeLayout();

  return (
    <MotionVStack
      alignItems="stretch"
      animate={{ [$drawerSlide.variable]: '0%' }}
      exit={{ [$drawerSlide.variable]: '100%' }}
      gridArea="selector"
      height={{ tablet: embedViewportHeight.tablet }}
      initial={{ [$drawerSlide.variable]: '100%' }}
      key="drawer"
      mr={{ tablet: '-100px' }}
      position="relative"
      pr={{ tablet: '100px' }}
      spacing={0}
      transform={{
        base: `translateY(${$drawerSlide.reference})`,
        tablet: `translateX(${$drawerSlide.reference})`,
      }}
      transition={transitions.drawer}
      {...props}
    />
  );
};

export interface CustomizeLayoutMotionProps extends PropsWithChildren<{}> {}

export const CustomizeLayoutMotion = (props: CustomizeLayoutMotionProps) => {
  const { transitions } = useCustomizeLayout();

  return <MotionConfig transition={transitions.layout} {...props} />;
};
